import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Section, Cell, Caption } from '@telegram-apps/telegram-ui'

import { api } from '../../lib/api'
import type { CalendarEvent } from '../../lib/types'
import { formatDateTime, formatDateLong, todayISO, addDaysISO } from '../../lib/format'
import PageHeader from '../../components/PageHeader'
import MonthGrid from '../../components/MonthGrid'
import { SkeletonList } from '../../components/Skeleton'

type View = 'day' | 'week' | 'month'

const dateOf = (e: CalendarEvent) => e.start.split('T')[0] || e.start

export default function DirectorCalendar() {
  const [view, setView] = useState<View>('week')
  const [selectedDate, setSelectedDate] = useState(todayISO())

  // Тянем широкое окно ОДИН раз и фильтруем на клиенте. Переключение День/Неделя/Месяц
  // происходит мгновенно, без повторных запросов и мигания списка.
  const { data: events, isLoading, isError, refetch } = useQuery<CalendarEvent[]>({
    queryKey: ['calendar'],
    queryFn: () => api.get<CalendarEvent[]>(`/calendar?days=120`),
  })

  const daysWithEvents = new Set((events ?? []).map(dateOf))

  const visibleEvents = useMemo(() => {
    const all = events ?? []
    if (view === 'week') {
      const from = todayISO()
      const to = addDaysISO(7)
      return all.filter((e) => { const d = dateOf(e); return d >= from && d < to })
    }
    // «День» и «Месяц» показывают выбранный день (по умолчанию — сегодня)
    return all.filter((e) => dateOf(e) === selectedDate)
  }, [events, view, selectedDate])

  return (
    <>
      <PageHeader title="Календарь" caption={formatDateLong(new Date())} />

      <div className="seg-row" style={{ ['--seg-cols' as any]: 3 }}>
        <button className={`seg-btn${view === 'day'   ? ' is-active' : ''}`} onClick={() => setView('day')}>День</button>
        <button className={`seg-btn${view === 'week'  ? ' is-active' : ''}`} onClick={() => setView('week')}>Неделя</button>
        <button className={`seg-btn${view === 'month' ? ' is-active' : ''}`} onClick={() => setView('month')}>Месяц</button>
      </div>

      {view === 'month' && (
        <MonthGrid selected={selectedDate} onSelect={setSelectedDate} daysWithEvents={daysWithEvents} />
      )}

      {isLoading ? (
        <SkeletonList count={5} />
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-state-title">Не удалось загрузить</div>
          <div className="empty-state-text">Проверьте соединение</div>
          <button className="btn-ghost" style={{ marginTop: 14 }} onClick={() => refetch()}>Повторить</button>
        </div>
      ) : view === 'month' ? (
        <Section header={formatDateLong(new Date(selectedDate + 'T00:00:00'))}>
          {visibleEvents.length === 0 ? (
            <Cell><Caption style={{ opacity: 0.6 }}>В этот день пусто</Caption></Cell>
          ) : (
            visibleEvents.map((e, i) => (
              <Cell key={i} before={<strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatDateTime(e.start)}</strong>} multiline>{e.summary}</Cell>
            ))
          )}
        </Section>
      ) : (
        <EventsList events={visibleEvents} />
      )}
    </>
  )
}

function EventsList({ events }: { events: CalendarEvent[] }) {
  if (events.length === 0) return <Cell><Caption style={{ opacity: 0.6 }}>Событий нет</Caption></Cell>

  const byDay: Record<string, CalendarEvent[]> = {}
  for (const e of events) {
    const day = e.start.split('T')[0] || e.start
    byDay[day] = byDay[day] || []
    byDay[day].push(e)
  }

  return (
    <>
      {Object.entries(byDay).map(([day, evs]) => (
        <Section key={day} header={formatDateLong(new Date(day + 'T00:00:00'))}>
          {evs.map((e, i) => (
            <Cell key={i} before={<strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatDateTime(e.start)}</strong>} multiline>{e.summary}</Cell>
          ))}
        </Section>
      ))}
    </>
  )
}
